<template>
  <footer class="app-footer">
    <div class="footer-content">
      <div class="footer-links">
        <a href="#" @click.prevent="openPrivacyPolicy">{{ $t('footerPrivacy') }}</a>
        <span class="divider">|</span>
        <a href="#" @click.prevent="openTermsOfService">{{ $t('footerTerms') }}</a>
        <span class="divider">|</span>
        <span class="about-link" @click="showAboutMenu = true">{{ $t('about') }}</span>
      </div>
    </div>

    <!-- 关于菜单弹窗 -->
    <van-popup v-model="showAboutMenu" round position="bottom" :style="{ minHeight: '120px' }">
      <div class="about-menu">
        <div class="about-menu-header">
          <div class="about-menu-indicator"></div>
        </div>

        <!-- 版本号 -->
        <div v-if="appVersion" class="about-menu-version">{{ $t('footerVersion', { version: appVersion }) }}</div>

        <!-- 公司信息（仅国内版，ICP 合规要求展示；国际版主体信息见隐私政策/服务条款文档） -->
        <div v-if="isCnRegion" class="about-menu-company">
          <div class="company-name">{{ $t('company_name') }}</div>
          <div class="company-copyright">&copy; {{ currentYear }} {{ $t('all_rights_reserved') }}</div>
          <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer" class="company-icp">京ICP备2024095000号-3</a>
        </div>
      </div>
    </van-popup>

    <!-- 隐私政策弹窗 -->
    <van-popup v-model="showPrivacyPopup" round position="bottom" :style="{ height: '85%' }">
      <div class="policy-popup">
        <div class="policy-header">
          <span class="policy-title">{{ $t('footerPrivacy') }}</span>
          <van-icon name="cross" size="20" color="#666" @click="showPrivacyPopup = false" />
        </div>
        <div class="policy-content" v-if="privacyContent" v-html="privacyContent"></div>
        <div class="policy-loading" v-else>
          <van-loading size="24px">{{ $t('loading') }}</van-loading>
        </div>
      </div>
    </van-popup>

    <!-- 服务条款弹窗 -->
    <van-popup v-model="showTermsPopup" round position="bottom" :style="{ height: '85%' }">
      <div class="policy-popup">
        <div class="policy-header">
          <span class="policy-title">{{ $t('footerTerms') }}</span>
          <van-icon name="cross" size="20" color="#666" @click="showTermsPopup = false" />
        </div>
        <div class="policy-content" v-if="termsContent" v-html="termsContent"></div>
        <div class="policy-loading" v-else>
          <van-loading size="24px">{{ $t('loading') }}</van-loading>
        </div>
      </div>
    </van-popup>
  </footer>
</template>

<script>
import { Icon, Popup, Loading } from 'vant'
import { loadPolicyDoc } from '@/i18n/policies/loader.js'

// 动态加载隐私政策内容
async function loadPrivacyContent(vm) {
  return loadPolicyDoc(vm, 'privacy')
}

// 动态加载服务条款内容
async function loadTermsContent(vm) {
  return loadPolicyDoc(vm, 'terms')
}

export default {
  name: 'Footer',
  components: {
    [Icon.name]: Icon,
    [Popup.name]: Popup,
    [Loading.name]: Loading
  },
  data() {
    return {
      showAboutMenu: false,
      showPrivacyPopup: false,
      showTermsPopup: false,
      privacyContent: '',
      termsContent: '',
      privacyContentLocale: '',
      termsContentLocale: ''
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    isCnRegion() {
      return this.$config && this.$config.region === 'cn'
    },
    appVersion() {
      return this.$config && this.$config.version
    }
  },
  methods: {
    async openPrivacyPolicy() {
      this.showAboutMenu = false
      this.showPrivacyPopup = true
      // 政策文档按语言缓存：global 版支持切换语言，切语言后需加载对应语言文档
      const locale = this.$i18n.locale
      if (!this.privacyContent || this.privacyContentLocale !== locale) {
        this.privacyContent = await loadPrivacyContent(this)
        this.privacyContentLocale = locale
      }
    },
    async openTermsOfService() {
      this.showAboutMenu = false
      this.showTermsPopup = true
      const locale = this.$i18n.locale
      if (!this.termsContent || this.termsContentLocale !== locale) {
        this.termsContent = await loadTermsContent(this)
        this.termsContentLocale = locale
      }
    }
  }
}
</script>

<style scoped>
.app-footer {
  margin-top: 20px;
  padding: 12px;
  text-align: center;
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
}

.footer-links {
  font-size: 11px;
}

.footer-links a {
  color: #8A8A8A;
  text-decoration: none;
}

.footer-links a:active {
  color: #E8543A;
}

.footer-links .divider {
  color: #ccc;
  margin: 0 8px;
}

.footer-links .about-link {
  color: #8A8A8A;
  cursor: pointer;
}

.footer-links .about-link:active {
  color: #8A8A8A;
}

.about-menu {
  padding: 16px 20px env(safe-area-inset-bottom, 16px);
}

.about-menu-header {
  padding: 8px 0;
  display: flex;
  justify-content: center;
}

.about-menu-indicator {
  width: 36px;
  height: 4px;
  background: #E0DDD8;
  border-radius: 2px;
}

.about-menu-company {
  padding: 20px 0;
  text-align: center;
}

.about-menu-version {
  padding: 20px 0;
  text-align: center;
  font-size: 12px;
  color: #8A8A8A;
}

.company-name {
  font-size: 12px;
  color: #8A8A8A;
  margin-bottom: 6px;
}

.company-copyright {
  font-size: 11px;
  color: #8A8A8A;
}

.company-icp {
  display: inline-block;
  margin-top: 6px;
  font-size: 11px;
  color: #8A8A8A;
  text-decoration: none;
}

.policy-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.policy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.policy-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
}

.policy-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  font-size: 13px;
  line-height: 1.8;
  color: #1A1A1A;
  text-align: left;
}

.policy-content ::v-deep h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 20px 0 10px 0;
  color: #1A1A1A;
}

.policy-content ::v-deep h3 {
  font-size: 14px;
  font-weight: 500;
  margin: 16px 0 8px 0;
  color: #4A4A4A;
}

.policy-content ::v-deep p {
  margin: 8px 0;
  color: #4A4A4A;
}

.policy-content ::v-deep ul {
  margin: 8px 0;
  padding-left: 20px;
}

.policy-content ::v-deep li {
  margin: 4px 0;
  color: #4A4A4A;
}

.policy-content ::v-deep a {
  color: #E8543A;
}

.policy-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Footer.vue